import React, { useEffect, useState } from 'react';
import { PaymentDialog, PriceItem } from '../PaymentDialog';
import { useStateSelector } from '../../../store/selectors';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import createNotification from '../../../utils/createNotification';
import { BuyerSelectionType } from '../../../wallet/models/BuyerSelectionType';
import { EngagementDocumentType } from '../../../wallet/models/EngagementDocumentType';

export interface EngagementDocumentAccessPaymentComponentProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    paymentSuccess: (transactionId: string) => void;
    engagementId: number;
    eventId: string;
    fteDvmsCount: number;
    pricePerFteDmv: number;
    totalPrice: number;
    creditableAmount: number;
    documentType: EngagementDocumentType;
    documentId: string;
}

const EngagementDocumentAccessPaymentComponent = (
    props: EngagementDocumentAccessPaymentComponentProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const stripe = useStripe();
    const elements = useElements();

    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);

    useEffect(() => {
        setOriginalServicePrice(props.totalPrice);
    }, []);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    const getTitleLabelByDocumentType = (): string => {
        switch (props.documentType) {
            case EngagementDocumentType.IdNda:
                return 'Seller Identification Fee';
            case EngagementDocumentType.DataNda:
                return 'PIP/PIQ Purchase Fee';
            case EngagementDocumentType.FinancialModel:
                return 'DFM Purchase Fee';
            default:
                return 'Engagement Document Access Fee';
        }
    };

    const priceList: PriceItem[] = [
        {
            priceType: 'summary',
            label: getTitleLabelByDocumentType(),
        },
        {
            priceType: 'regular',
            label: 'Fee per FTE DVMs:',
            value: props.pricePerFteDmv,
            valueType: 'currency',
        },
        {
            priceType: 'regular',
            label: 'Seller FTE DVM:',
            value: props.fteDvmsCount,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: props.totalPrice,
            valueType: 'currency',
        },
        ...(props.creditableAmount
            ? [
                  {
                      priceType: 'regular',
                      label: 'Creditable Amount:',
                      value: props.creditableAmount,
                      valueType: 'currency',
                  } as PriceItem,
              ]
            : []),
    ];

    useEffect(() => {
        if (props.creditableAmount) {
            priceList.push();
        }
    }, []);

    const directPayment = () => payIdNdaExecutionFee(false);

    const payIdNdaExecutionFee = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        axios
            .post('/api/MarketplaceCommerce/DocumentAccessPurchase', {
                walletId: 0,
                engagementId: props.engagementId,
                eventId: props.eventId,
                useWalletBalance: !!appliedCredits,
                documentType: props.documentType,
                documentId: props.documentId,
                walletAmount: appliedCredits,
                useSavedPaymentMethod: shouldUseSavedPaymentMethod,
                source: paymentTokenId,
                type: BuyerSelectionType.FinancialModel,
                totalPrice: originalServicePrice,
            })
            .then((response: any) => {
                if (response?.response?.status == 402) {
                    createNotification(
                        response.response.data?.title ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                    setIsPaymentInProcess(false);
                    props.setIsVisible(false);
                } else {
                    handlePricingUpdatedError(response);
                    setTimeout(() => {
                        setIsPaymentInProcess(false);
                        props.paymentSuccess(response.data);
                    }, 3000);
                }
            })
            .catch((err: any) => console.log(err));
    };

    const handlePricingUpdatedError = (response: any) => {
        if (response.response?.status === 422) {
            createNotification(
                'Buyer Seleciton Fee has been changed, please try again',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Price changed');
        }

        if (response.response?.status === 500) {
            createNotification(
                response.response.data?.title ??
                    'An unexpected error occured. Please try again later',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Bad request');
        }
    };

    return (
        <div className="id-nda-execution-fee-payment-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={priceList}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                chargeRequest={payIdNdaExecutionFee}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                isAuthorizationRequired={false}></PaymentDialog>
        </div>
    );
};
export default EngagementDocumentAccessPaymentComponent;
